import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameButton, GameCard, GameHero, GameIcon, SegmentedControl, StatTile } from "../../components/game";
import {
  getMyEventInvitations,
  confirmEventInvitation,
  declineEventInvitation,
  getEvent,
  getEventNotes,
  type EventInvitation,
  type EventNote,
} from "../../services/api";

const invitationStatusLabels: Record<string, { label: string; color: string }> = {
  invited: { label: "Invitat", color: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Confirmat", color: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Refuzat", color: "bg-red-100 text-red-700" },
};

export default function AppEvents() {
  const { t } = useTranslation();

  const [invitations, setInvitations] = useState<EventInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "invited" | "confirmed" | "declined">("all");

  // Event details modal
  const [selectedInvitation, setSelectedInvitation] = useState<EventInvitation | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState<EventNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const data = await getMyEventInvitations();
      setInvitations(data.invitations);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      toast.error("Nu s-au putut incarca invitatiile");
    }
    setLoading(false);
  };

  const filteredInvitations = filter === "all"
    ? invitations
    : invitations.filter(inv => inv.status === filter);

  const openInvitationModal = async (invitation: EventInvitation) => {
    setSelectedInvitation(invitation);
    setShowModal(true);
    setDeclineReason("");

    if (invitation.status === "confirmed" && invitation.event) {
      loadNotes(invitation.event.id);
    }
  };

  const loadNotes = async (eventId: string) => {
    setLoadingNotes(true);
    try {
      const data = await getEventNotes(eventId);
      setNotes(data.notes);
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
    setLoadingNotes(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInvitation(null);
    setNotes([]);
  };

  const handleConfirm = async () => {
    if (!selectedInvitation) return;

    setProcessing(true);
    try {
      await confirmEventInvitation(selectedInvitation.id);
      toast.success("Participare confirmata!");
      await fetchInvitations();
      closeModal();
    } catch (error) {
      console.error("Failed to confirm:", error);
      toast.error("Nu s-a putut confirma participarea");
    }
    setProcessing(false);
  };

  const handleDecline = async () => {
    if (!selectedInvitation) return;

    setProcessing(true);
    try {
      await declineEventInvitation(selectedInvitation.id, declineReason || undefined);
      toast.success("Invitatie refuzata");
      setShowDeclineModal(false);
      await fetchInvitations();
      closeModal();
    } catch (error) {
      console.error("Failed to decline:", error);
      toast.error("Nu s-a putut refuza invitatia");
    }
    setProcessing(false);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getDaysUntil = (dateStr: string) => {
    const now = new Date();
    const diff = new Date(dateStr).getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return "Trecut";
    if (days === 0) return "Astazi";
    if (days === 1) return "Maine";
    return `in ${days} zile`;
  };

  const pendingCount = invitations.filter(i => i.status === "invited").length;
  const confirmedCount = invitations.filter(i => i.status === "confirmed").length;

  return (
    <MobileAppLayout>
      <div className="p-4 space-y-4">
        {/* Header — unified gradient hero (matches Sessions) */}
        <GameHero flow className="-mx-4 -mt-4 mb-1 rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold">{t("events.title", "Evenimente")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">{t("events.subtitle", "Invitațiile și evenimentele tale")}</p>
        </GameHero>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <StatTile icon="bulb" label="Invitații noi" value={pendingCount} color="#f59e0b" />
          <StatTile icon="check" label="Confirmate" value={confirmedCount} color="#10b981" />
        </div>

        {/* Filter */}
        <SegmentedControl<"all" | "invited" | "confirmed" | "declined">
          className="flex w-full [&>button]:flex-1"
          options={[
            { value: "all", label: "Toate" },
            { value: "invited", label: "Noi" },
            { value: "confirmed", label: "Confirmate" },
            { value: "declined", label: "Refuzate" },
          ]}
          value={filter}
          onChange={setFilter}
        />

        {/* Invitations List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-4 border-indigo-500 rounded-full border-t-transparent animate-spin" />
          </div>
        ) : filteredInvitations.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
              <GameIcon name="star" className="w-8 h-8" />
            </div>
            <p className="text-slate-500">
              {filter === "all"
                ? "Nu ai invitatii la evenimente"
                : `Nu ai invitatii ${invitationStatusLabels[filter]?.label.toLowerCase()}`}
            </p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {filteredInvitations.map(invitation => (
              <GameCard
                key={invitation.id}
                onClick={() => openInvitationModal(invitation)}
                className="p-4 cursor-pointer active:translate-y-[2px] transition-transform"
              >
                <div className="flex gap-4">
                  {invitation.event?.photo_url ? (
                    <img
                      src={invitation.event.photo_url}
                      alt={invitation.event.title}
                      className="object-cover w-20 h-20 rounded-2xl"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-2xl">
                      <GameIcon name="star" className="w-8 h-8" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-slate-800 line-clamp-1">
                        {invitation.event?.title || "Eveniment"}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full shrink-0 ${invitationStatusLabels[invitation.status]?.color}`}>
                        {invitationStatusLabels[invitation.status]?.label}
                      </span>
                    </div>

                    {invitation.event && (
                      <>
                        <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>{formatDate(invitation.event.start_date)}</span>
                          {invitation.event.event_time && <span>@ {invitation.event.event_time}</span>}
                        </div>

                        {invitation.event.location_city && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <span>{invitation.event.location_city}</span>
                          </div>
                        )}

                        <div className="mt-2">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full ${
                            new Date(invitation.event.start_date) > new Date()
                              ? "bg-indigo-50 text-indigo-600"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                            {getDaysUntil(invitation.event.start_date)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </GameCard>
            ))}
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {showModal && selectedInvitation && selectedInvitation.event && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-0 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-0"
          onClick={closeModal}
        >
          <div
            className="w-full max-w-lg max-h-full sm:max-h-[90dvh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header. Only an event WITH a photo earns a tall banner — the
                placeholder gradient wasted 128px of a short modal, which is
                what made the header read as big and empty. */}
            <div className="relative shrink-0">
              {selectedInvitation.event.photo_url ? (
                <>
                  <img
                    src={selectedInvitation.event.photo_url}
                    alt={selectedInvitation.event.title}
                    className="object-cover w-full h-28"
                  />
                  <div className="absolute bottom-3 left-3">
                    <span className={`px-3 py-1 text-sm font-bold rounded-full ${invitationStatusLabels[selectedInvitation.status]?.color}`}>
                      {invitationStatusLabels[selectedInvitation.status]?.label}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center w-full h-12 px-4 bg-gradient-to-r from-indigo-500 to-blue-600">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${invitationStatusLabels[selectedInvitation.status]?.color}`}>
                    {invitationStatusLabels[selectedInvitation.status]?.label}
                  </span>
                </div>
              )}
              <button
                onClick={closeModal}
                className="absolute p-2 bg-white rounded-full shadow-lg top-2 right-3"
              >
                <GameIcon name="x" className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Info, notes and the decline reason share ONE scroll container,
                so a long description can no longer push the confirm/decline
                buttons off the bottom of the modal. */}
            <div className="flex-1 overflow-y-auto overscroll-contain">

            {/* Event Info */}
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-extrabold text-slate-800">{selectedInvitation.event.title}</h2>

              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>
                    {formatDate(selectedInvitation.event.start_date)}
                    {selectedInvitation.event.event_time && ` @ ${selectedInvitation.event.event_time}`}
                  </span>
                </div>

                {selectedInvitation.event.location_address && (
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <svg className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <span>
                      {selectedInvitation.event.location_address}
                      {selectedInvitation.event.location_city && `, ${selectedInvitation.event.location_city}`}
                    </span>
                  </div>
                )}

                {selectedInvitation.coach && (
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>Invitat de {selectedInvitation.coach.full_name || "—"}</span>
                  </div>
                )}
              </div>

              {selectedInvitation.event.description && (
                <p className="mt-3 text-sm text-slate-600">{selectedInvitation.event.description}</p>
              )}

              {selectedInvitation.event.specifications && (
                <div className="p-3 mt-3 text-sm bg-slate-50 rounded-2xl">
                  <p className="font-bold text-slate-700 mb-1">Specificații:</p>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedInvitation.event.specifications}</p>
                </div>
              )}
            </div>

            {/* Notes (only for confirmed) */}
            {selectedInvitation.status === "confirmed" && (
              <div className="p-4">
                <h3 className="font-bold text-slate-700 mb-3">Note</h3>
                {loadingNotes ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="w-6 h-6 border-2 border-indigo-500 rounded-full border-t-transparent animate-spin" />
                  </div>
                ) : notes.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-4">Nu există note</p>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => (
                      <div
                        key={note.id}
                        className={`p-3 rounded-2xl ${
                          note.author_role === "club_admin"
                            ? "bg-blue-50 border-l-4 border-blue-500"
                            : "bg-emerald-50 border-l-4 border-emerald-500"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-bold ${
                            note.author_role === "club_admin" ? "text-blue-600" : "text-emerald-600"
                          }`}>
                            {note.author?.full_name || ""}
                            {note.author_role === "club_admin" && " (Admin)"}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(note.created_at).toLocaleString("ro-RO", {
                              day: "2-digit",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Decline reason info */}
            {selectedInvitation.status === "declined" && selectedInvitation.decline_reason && (
              <div className="p-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  <span className="font-bold">Motiv refuz:</span> {selectedInvitation.decline_reason}
                </p>
              </div>
            )}
            </div>

            {/* Action Buttons (only for pending invitations) — pinned below
                the scroll area so they are always reachable. */}
            {selectedInvitation.status === "invited" && (
              <div className="p-4 border-t border-slate-200 space-y-2 shrink-0">
                <GameButton variant="mint" onClick={handleConfirm} disabled={processing}>
                  {processing ? "Se proceseaza..." : "Confirm participarea"}
                </GameButton>
                <GameButton variant="danger" onClick={() => setShowDeclineModal(true)} disabled={processing}>
                  Nu pot participa
                </GameButton>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDeclineModal(false)}>
          <div className="w-full max-w-sm bg-white rounded-3xl p-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-slate-800 mb-4">Nu poți participa?</h3>
            <p className="text-sm text-slate-600 mb-3">Poți adauga un motiv (opțional):</p>
            <textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Ex: Am un alt eveniment in acea zi..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
            />
            <div className="flex gap-2 mt-4">
              <GameButton variant="ghost" className="flex-1" onClick={() => setShowDeclineModal(false)}>
                Anulează
              </GameButton>
              <GameButton variant="danger" className="flex-1" onClick={handleDecline} disabled={processing}>
                {processing ? "..." : "Refuza invitatie"}
              </GameButton>
            </div>
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
